import React, { useState } from "react";
import { Grid, Card, Icon, IconButton } from "@mui/material";
import AsyncSelect from "react-select/async";
import toast from "react-hot-toast";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import UsersApi from "../../Classes/API/UsersApi";
import ServicesApi from "../../Classes/API/Services/ServicesApi";
import searchUsersReq from "../../Classes/Models/Requests/Users/SearchUser";
import UserTypes from "../../Classes/Models/Requests/Users/UserTypes";
import CreatePackageReq from "../../Classes/Models/Requests/AccountingSessions/CreatePackageReq";
import SessionsApi from "../../Classes/API/Administration/SessionsApi";
import Swal from "sweetalert2";

import ServiceSelector from "./ServiceSelector";

const asyncSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 44,
    borderRadius: 8,
    borderColor: "#d2d6da",
    fontSize: "14px",
    boxShadow: "none",
    "&:hover": { borderColor: "#1961e7ff" },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0px 12px",
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: "14px",
    color: "#adb5bd",
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    borderRadius: 8,
  }),
};

export default function NewServiceBundle() {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showPackageSelector, setShowPackageSelector] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadPatientOptions = async (v) => {
    try {
      const req = new searchUsersReq();
      req.name = v;
      req.userType = new UserTypes().Patient;
      const res = await UsersApi.SearchUsers(req);
      const list = res.data?.list || [];
      return list.map((p) => ({ value: p, label: p.name }));
    } catch (err) {
      return [];
    }
  };

  const loadPackageOptions = async (v) => {
    if (!selectedPatient) return [];
    try {
      const req = new CreatePackageReq();
      req.patientId = selectedPatient.getPatient?.id || selectedPatient.id;
      req.packageName = v;
      const res = await SessionsApi.SearchPackages(req);
      const list = res.data?.list || [];
      return list.map((pkg) => ({
        value: pkg,
        label: `${pkg.packageName} (ID: ${pkg.id}, Final Price: ${pkg.finalPrice} EGP)`,
      }));
    } catch (err) {
      return [];
    }
  };

  const handleAddServiceToQueue = (newService) => {
    setSelectedServices([...selectedServices, newService]);
  };

  const handleRemoveService = (index) => {
    setSelectedServices(selectedServices.filter((_, i) => i !== index));
  };

  const grandTotal = selectedServices.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );

  const handleCreateBundle = async () => {
    if (!selectedPatient) return toast.error("Please select a patient");
    if (selectedPackage == null) {
      const result = await Swal.fire({
        title: "Link to Package?",
        text: "You are creating this service bundle without linking it to any package. Do you want to proceed?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Create Bundle",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    setIsSubmitting(true);
    const loadId = toast.loading("Creating bundle...");

    const patientId = selectedPatient.getPatient?.id || selectedPatient.id;

    const payload = {
      patientId: patientId,
      totalAmount: grandTotal,
      items: selectedServices.map((s) => ({
        serviceId: s.serviceId,
        unitPrice: s.price,
        frequency: s.frequency,
        quantity: s.quantity,
        numWeeks: s.numWeeks,
        selectedDays: s.weekDaysLabels || s.selectedDays,
        startDate: s.startDate,
      })),
    };

    try {
      const response = await ServicesApi.CreateBundle(payload);
      if (response.status === "Success") {
        let linkedMsg = "";

        if (selectedPackage) {
          const packageId = selectedPackage.value.id;
          const lastBundleRes = await ServicesApi.LastServiceBundle(patientId, packageId);
          if (lastBundleRes.status === "Success" && lastBundleRes.data?.serviceBundle?.id) {
            const bundleId = lastBundleRes.data.serviceBundle.id;

            const linkRes = await ServicesApi.LinkBundleToPackage({
              bundleId: bundleId,
              packageId: packageId,
            });

            if (linkRes.status === "Success") {
              linkedMsg = " and linked to package successfully";
            } else {
              toast.error(
                linkRes.message || "Failed to link bundle to package",
                { id: loadId },
              );
            }
          }
        }

        toast.success(`Bundle created successfully${linkedMsg}!`, {
          id: loadId,
        });
        setSelectedServices([]);
        setSelectedPatient(null);
        setSelectedPackage(null);
        setShowPackageSelector(false);
      } else {
        toast.error(response.message || "Failed to create bundle", {
          id: loadId,
        });
      }
    } catch (error) {
      toast.error("An error occurred during creation", { id: loadId });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3}>
        <Card sx={{ borderRadius: "16px", border: "1px solid #f0f2f5" }}>
          <MDBox p={4}>
            {/* Header Section */}
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              mb={4}
            >
              <MDBox>
                <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Icon fontSize="medium" color="info">
                    assignment_add
                  </Icon>
                  <MDTypography variant="h4" fontWeight="bold">
                    Bundle Builder
                  </MDTypography>
                </MDBox>
                <MDTypography variant="button" color="text">
                  Design a customized treatment plan for your patient.
                </MDTypography>
              </MDBox>

              <MDBox
                variant="gradient"
                bgColor="info"
                borderRadius="xl"
                p={2}
                textAlign="center"
                minWidth="150px"
              >
                <MDTypography
                  variant="caption"
                  color="white"
                  fontWeight="medium"
                  textTransform="uppercase"
                >
                  Total Amount
                </MDTypography>
                <MDTypography variant="h3" color="white" fontWeight="bold">
                  {grandTotal.toLocaleString()}{" "}
                  <small style={{ fontSize: "14px" }}>EGP</small>
                </MDTypography>
              </MDBox>
            </MDBox>

            {/* Patient Selection */}
            <MDBox mb={4} p={3} bgColor="grey-100" borderRadius="lg">
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <MDTypography variant="h6" fontWeight="bold">
                  Patient Assignment
                </MDTypography>
                {selectedPatient && (
                  <MDButton
                    variant="outlined"
                    color={showPackageSelector ? "error" : "info"}
                    size="small"
                    onClick={() => {
                      setShowPackageSelector(!showPackageSelector);
                      if (showPackageSelector) {
                        setSelectedPackage(null);
                      }
                    }}
                  >
                    <Icon>{showPackageSelector ? "close" : "add"}</Icon>&nbsp;
                    {showPackageSelector
                      ? "Remove Package Link"
                      : "Link to Package (Optional)"}
                  </MDButton>
                )}
              </MDBox>
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={loadPatientOptions}
                placeholder="Patient's name only"
                isClearable
                value={
                  selectedPatient
                    ? { value: selectedPatient, label: selectedPatient.name }
                    : null
                }
                onChange={(e) => {
                  setSelectedPatient(e ? e.value : null);
                  setSelectedPackage(null);
                  setShowPackageSelector(false);
                }}
                styles={asyncSelectStyles}
              />

              {selectedPatient && showPackageSelector && (
                <MDBox mt={3}>
                  <MDTypography variant="h6" fontWeight="bold" mb={2}>
                    Package Assignment (Optional)
                  </MDTypography>
                  <AsyncSelect
                    key={selectedPatient.getPatient?.id || selectedPatient.id}
                    cacheOptions
                    defaultOptions
                    loadOptions={loadPackageOptions}
                    placeholder="Select a package..."
                    isClearable
                    value={selectedPackage}
                    onChange={(e) => setSelectedPackage(e)}
                    styles={asyncSelectStyles}
                  />
                </MDBox>
              )}
            </MDBox>

            {/* Service Selector Component */}
            <MDBox mb={4}>
              <MDTypography
                variant="h6"
                fontWeight="bold"
                mb={2}
                display="flex"
                alignItems="center"
                gap={1}
              >
                <Icon color="dark">library_add</Icon> Add Services to Queue
              </MDTypography>
              <Card
                sx={{ p: 3, border: "1px solid #dee2e6", boxShadow: "none" }}
              >
                <ServiceSelector
                  onServiceAdd={handleAddServiceToQueue}
                  actionIcon="add"
                  actionColor="dark"
                />
              </Card>
            </MDBox>

            {/* Queue Table */}
            <MDBox
              mt={4}
              sx={{
                borderRadius: "12px",
                overflow: "hidden",
                border: "1px solid #f0f2f5",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ backgroundColor: "#f8f9fa" }}>
                  <tr>
                    {[
                      "Service",
                      "Start Date",
                      "Pattern",
                      "Details",
                      "Sessions",
                      "Subtotal",
                      "",
                    ].map((head) => (
                      <th
                        key={head}
                        style={{ padding: "16px", textAlign: "left" }}
                      >
                        <MDTypography
                          variant="caption"
                          color="text"
                          fontWeight="bold"
                          textTransform="uppercase"
                        >
                          {head}
                        </MDTypography>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {selectedServices.map((item, index) => (
                    <tr
                      key={index}
                      style={{ borderBottom: "1px solid #f0f2f5" }}
                    >
                      <td style={{ padding: "16px" }}>
                        <MDTypography variant="button" fontWeight="bold">
                          {item.name}
                        </MDTypography>
                      </td>
                      <td style={{ padding: "16px" }}>
                        <MDTypography variant="button">
                          {item.startDate
                            ? new Intl.DateTimeFormat("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "2-digit",
                              }).format(new Date(item.startDate))
                            : "N/A"}
                        </MDTypography>
                      </td>
                      <td style={{ padding: "16px" }}>
                        <MDBox
                          bgColor={
                            item.frequency === "weekly" ? "secondary" : "info"
                          }
                          color="white"
                          borderRadius="md"
                          px={1.5}
                          py={0.5}
                          display="inline-block"
                        >
                          <MDTypography
                            variant="caption"
                            color="white"
                            fontWeight="bold"
                          >
                            {item.frequency.toUpperCase()}
                          </MDTypography>
                        </MDBox>
                      </td>
                      <td style={{ padding: "16px" }}>
                        <MDTypography variant="caption" color="text">
                          {item.frequency === "weekly"
                            ? `Every [${item.selectedDays?.join(", ")}] for ${
                                item.numWeeks
                              } weeks`
                            : `Daily for ${item.quantity} sessions`}
                        </MDTypography>
                      </td>
                      <td style={{ padding: "16px" }}>
                        <MDTypography variant="button" fontWeight="medium">
                          {item.quantity}
                        </MDTypography>
                      </td>
                      <td style={{ padding: "16px" }}>
                        <MDTypography variant="button" fontWeight="bold">
                          {(item.price * item.quantity).toLocaleString()} EGP
                        </MDTypography>
                      </td>
                      <td style={{ padding: "16px", textAlign: "right" }}>
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveService(index)}
                        >
                          <Icon>delete_outline</Icon>
                        </IconButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {selectedServices.length === 0 && (
                <MDBox py={8} textAlign="center" bgColor="white">
                  <Icon
                    sx={{
                      fontSize: "48px !important",
                      color: "#ced4da",
                      mb: 1,
                    }}
                  >
                    receipt_long
                  </Icon>
                  <MDTypography variant="h6" color="text" fontWeight="regular">
                    No services added to the bundle yet.
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>

            {/* Footer Actions */}
            <MDBox mt={5} display="flex" justifyContent="flex-end" gap={2}>
              <MDButton
                variant="outlined"
                color="error"
                onClick={() => setSelectedServices([])}
                disabled={isSubmitting}
              >
                Reset All
              </MDButton>
              <MDButton
                variant="gradient"
                color="success"
                size="large"
                sx={{ px: 8 }}
                disabled={
                  selectedServices.length === 0 ||
                  !selectedPatient ||
                  isSubmitting
                }
                onClick={handleCreateBundle}
              >
                {isSubmitting ? "Processing..." : "Confirm & Create Bundle"}
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}
